import { useLocation } from 'react-router-dom';
import { AppRoutes } from '../../Routes';
import { useMemo } from 'react';
import { ContentCollectionType } from '../../API';

// Sections that render the content search panel. Two routes resolving to the same scope also share their
// filters, so every section that must start from its own filters needs its own scope.
export const ContentSearchScopes = {
  metadata: 'metadata',
  mixedCollections: 'collections.mixed',
  vodCollections: 'collections.vod',
  top10Collections: 'collections.top10',
  unknown: 'unknown'
} as const;

// Sections that can only work with a subset of the content types. Their searches have to be limited to these
// types as well, otherwise an empty content type filter brings back every type in the index.
// The first entry is what the section preselects on load, so new types go at the end.
export const contentTypesAllowedByScope: Record<string, ContentCollectionType[]> = {
  [ContentSearchScopes.metadata]: [
    ContentCollectionType.CLIP,
    ContentCollectionType.SERIES,
    ContentCollectionType.NOVELA,
    ContentCollectionType.SHORT_NOVELA,
    ContentCollectionType.MOVIE
  ]
};

export interface IUseRoute {
  isUnderMetadataRoute: boolean;
  contentSearchScope: string;
  contentTypesAllowedForScope: ContentCollectionType[];
}

export function useRoute(): IUseRoute {
  const location = useLocation();

  const isUnderMetadataRoute = useMemo(() => {
    return location.pathname.includes(AppRoutes.metadataManager);
  }, [location.pathname]);

  const contentSearchScope = useMemo(() => {
    // AppRoutes is read lazily because Routes imports back into this module
    const scopesByRoute: Array<[string, string]> = [
      [AppRoutes.metadataManager, ContentSearchScopes.metadata],
      [AppRoutes.collections(), ContentSearchScopes.mixedCollections],
      [AppRoutes.vodCollections(), ContentSearchScopes.vodCollections],
      [AppRoutes.top10Collections(), ContentSearchScopes.top10Collections]
    ];

    const matchedScope = scopesByRoute.find(([route]) => location.pathname.startsWith(route));
    if (matchedScope) return matchedScope[1];

    // Fall back to the section segment so the scope stays stable while navigating within it
    const [, , section] = location.pathname.split('/');
    return section || ContentSearchScopes.unknown;
  }, [location.pathname]);

  const contentTypesAllowedForScope = useMemo(
    () => contentTypesAllowedByScope[contentSearchScope] || [],
    [contentSearchScope]
  );

  return {
    isUnderMetadataRoute,
    contentSearchScope,
    contentTypesAllowedForScope
  };
}
